import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ICellRendererParams } from 'ag-grid-community';
import type { ICellRendererAngularComp } from 'ag-grid-angular';

type ParamOrResolver<T> = T | ((params: ICellRendererParams) => T);

/**
 * This component is designed to be used as a cellRenderer in AG Grid column definitions.
 * All icons referenced in this component are already available in the 'src/assets/icons/' directory:
 * - Status icons: 'circle-check2.svg', 'in-process-icon.svg', 'in-transit-icon.svg', 'open-icon.svg', 'circle-xmark.svg'
 */

export interface GainsColoredTextCellComponentParams extends ICellRendererParams {
  icon?: ParamOrResolver<string>;
  text?: ParamOrResolver<string>;
  color?: ParamOrResolver<string>;
  iconColor?: ParamOrResolver<string>;
  textColor?: ParamOrResolver<string>;
  backgroundColor?: ParamOrResolver<string>;
  borderColor?: ParamOrResolver<string>;
  className?: ParamOrResolver<string>;
  showIcon?: boolean;
  showText?: boolean;
}

@Component({
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './gains-colored-text-cell.component.html',
  styleUrl: './gains-colored-text-cell.component.scss'
})
export class GainsColoredTextCellComponent implements ICellRendererAngularComp {
  params!: GainsColoredTextCellComponentParams;
  icon?: string;
  text?: string;
  color?: string;
  iconColor?: string;
  textColor?: string;
  backgroundColor?: string;
  borderColor?: string;
  className?: string;
  showIcon = true;
  showText = true;

  agInit(params: GainsColoredTextCellComponentParams): void {
    this.setValues(params);
  }

  refresh(params: GainsColoredTextCellComponentParams): boolean {
    this.setValues(params);
    return true;
  }

  private setValues(params: GainsColoredTextCellComponentParams): void {
    this.params = params;
    this.icon = this.resolveValue(params.icon, params) || '';
    this.text = this.resolveValue(params.text, params) || `${params.value ?? ''}`;
    this.color = this.resolveValue(params.color, params) || '';
    this.iconColor = this.resolveValue(params.iconColor, params) || this.color || '';
    this.textColor = this.resolveValue(params.textColor, params) || this.color || '';
    this.backgroundColor = this.resolveValue(params.backgroundColor, params) || '';
    this.borderColor = this.resolveValue(params.borderColor, params) || '';
    this.className = this.resolveValue(params.className, params) || '';
    this.showIcon = params.showIcon !== false;
    this.showText = params.showText !== false;
  }

  private resolveValue<T>(value: ParamOrResolver<T> | undefined, params: ICellRendererParams): T | undefined {
    if (typeof value === 'function') {
      return (value as (rendererParams: ICellRendererParams) => T)(params);
    }
    return value;
  }
}
